import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faMapMarkerAlt, faCalculator } from '@fortawesome/free-solid-svg-icons';
import '../../styles/sections/cta/ContactCTATwoColumnSection.css';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import { validateLeadForm } from '../../../core/validations/LeadFormValidation';
import { createLead } from '../../../core/services/LeadService';
import { useI18n } from '../../../i18n/I18nProvider';
import Select from '../../components/forms/Select';
import '../../styles/components/forms/Select.css';

const ContactCTATwoColumnSection = ({
  title,
  subtitle,
  contactInfo,
  className = ''
}) => {
  const { t } = useI18n();
  
  // Use translations if props are not provided
  const sectionTitle = title || t('contact.title');
  const sectionSubtitle = subtitle || t('contact.subtitle');
  const defaultContactInfo = contactInfo || {
    items: [
      { label: t('contact.whatsapp'), value: '+1 (786) 828-2040', href: 'https://wa.me/17868282040', icon: faWhatsapp },
      { label: t('contact.sendBrief'), value: 'jiddufilms@gmail.com', href: 'mailto:jiddufilms@gmail.com', icon: faEnvelope },
      { label: t('contact.location'), value: 'Miami, Florida, EEUU', icon: faMapMarkerAlt }
    ]
  };
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    // Reuse existing validation mapping: service goes into message
    const validationErrors = validateLeadForm({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      message: formData.service || t('contact.form.servicePlaceholder'),
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone || null,
        message: `Servicio: ${formData.service || 'N/A'}`,
      };
      const res = await createLead(payload);
      if (res.success) {
        setSubmitted(true);
        setFormData({ firstName: '', lastName: '', email: '', phone: '', service: '' });
      } else {
        setErrors({ submit: res.message || t('contact.form.error') });
      }
    } catch (err) {
      setErrors({ submit: t('contact.form.error') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={`contact-cta-two-column ${className}`}>
      <div className="contact-cta-two-column__container">
        <div className="contact-cta-two-column__content">
          <div className="contact-cta-two-column__header">
            <h2 className="contact-cta-two-column__title">{sectionTitle}</h2>
            <p className="contact-cta-two-column__subtitle">{sectionSubtitle}</p>
          </div>

          <div className="contact-cta-two-column__columns">
            <div className="contact-cta-two-column__column contact-cta-two-column__column--left">
              <div className="contact-cta-two-column__contact-info">
           
                <div className="contact-cta-two-column__contact-items">
                  {(defaultContactInfo.items || []).map((item, idx) => (
                    <div key={`contact-item-${idx}`} className="contact-cta-two-column__contact-item">
                      <div className="contact-cta-two-column__contact-icon">
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div className="contact-cta-two-column__contact-label">{item.label}</div>
                      {item.href ? (
                        <a className="contact-cta-two-column__contact-value" href={item.href} target="_blank" rel="noreferrer">
                          {item.value}
                        </a>
                      ) : (
                        <div className="contact-cta-two-column__contact-value">{item.value}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="contact-cta-two-column__column contact-cta-two-column__column--right">
              <div className="contact-cta-two-column__form-card">
                <div className="contact-cta-two-column__form-title-container">
                  <FontAwesomeIcon icon={faCalculator} className="contact-cta-two-column__form-icon" />
                  <h3 className="contact-cta-two-column__form-title">{t('contact.form.title')}</h3>
                </div>
                {submitted ? (
                  <div className="contact-cta-two-column__success">
                    {t('contact.form.success')}
                  </div>
                ) : (
                  <form className="contact-cta-two-column__form" onSubmit={handleSubmit}>
                    <div className="contact-cta-two-column__form-row">
                      <div className="contact-cta-two-column__form-group">
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder={t('contact.form.firstName')}
                          className={errors.firstName ? 'error' : ''}
                        />
                        {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                      </div>
                      <div className="contact-cta-two-column__form-group">
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder={t('contact.form.lastName')}
                          className={errors.lastName ? 'error' : ''}
                        />
                        {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                      </div>
                    </div>
                    <div className="contact-cta-two-column__form-group">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contact.form.email')}
                        className={errors.email ? 'error' : ''}
                      />
                      {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>
                    <div className="contact-cta-two-column__form-group">
                      <Select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        placeholder={t('contact.form.servicePlaceholder')}
                        options={[
                          { value: t('services.editing.title'), label: t('services.editing.title') },
                          { value: t('services.visualDirection.title'), label: t('services.visualDirection.title') },
                          { value: t('services.liveCommunication.title'), label: t('services.liveCommunication.title') },
                          { value: t('services.fullProduction.title'), label: t('services.fullProduction.title') },
                          { value: t('services.studioMobile.title'), label: t('services.studioMobile.title') },
                          { value: t('services.strategicBts.title'), label: t('services.strategicBts.title') },
                          { value: t('services.other'), label: t('services.other') },
                        ]}
                        className={errors.message ? 'error' : ''}
                        error={Boolean(errors.message)}
                      />
                      {errors.message && <span className="error-message">{errors.message}</span>}
                    </div>
                    {errors.submit && <div className="error-message submit">{errors.submit}</div>}
                    <PrimaryButton type="submit" disabled={submitting} className="contact-cta-two-column__submit">
                      {submitting ? t('contact.form.sending') : t('contact.form.send')}
                    </PrimaryButton>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactCTATwoColumnSection;


